import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { auditTrailService } from '../../../lib/auditTrailService';
import { useAuth } from '../../../contexts/AuthContext';

const IncidentCaseManagement = ({ events, onSelectCase, onRefresh }) => {
  const { userProfile } = useAuth();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolving, setResolving] = useState(false);

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'var(--color-error)',
      error: 'var(--color-error)',
      warning: 'var(--color-warning)',
      info: 'var(--color-info)'
    };
    return colors?.[severity] || 'var(--color-muted)';
  };

  const getEventTypeIcon = (eventType) => {
    const icons = {
      failed_login: 'Lock',
      suspicious_activity: 'AlertTriangle',
      unauthorized_access: 'ShieldAlert',
      data_breach_attempt: 'Database',
      privilege_escalation: 'TrendingUp',
      account_lockout: 'UserX',
      password_reset: 'Key',
      session_hijacking: 'Wifi'
    };
    return icons?.[eventType] || 'AlertCircle';
  };

  const handleResolveEvent = async () => {
    if (!selectedEvent || !resolutionNotes?.trim()) return;

    setResolving(true);
    try {
      const { error } = await auditTrailService?.resolveSecurityEvent(
        selectedEvent?.id,
        userProfile?.id,
        resolutionNotes
      );

      if (!error) {
        setSelectedEvent(null);
        setResolutionNotes('');
        onRefresh?.();
      }
    } catch (error) {
      console.error('Error resolving event:', error);
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Cases List */}
      <div className="lg:col-span-2 card">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-heading font-semibold text-foreground">
            Active Incident Cases
          </h3>
          <span className="text-sm text-muted-foreground">
            {events?.length || 0} open cases
          </span>
        </div>

        <div className="space-y-3">
          {events?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="CheckCircle" size={48} className="mx-auto mb-4 text-success" />
              <p className="text-muted-foreground">No active security incidents</p>
            </div>
          ) : (
            events?.map((event) => (
              <div
                key={event?.id}
                onClick={() => {
                  setSelectedEvent(event);
                  onSelectCase?.(event);
                }}
                className={`p-4 rounded-lg border cursor-pointer transition-all duration-250 ${
                  selectedEvent?.id === event?.id
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${getSeverityColor(event?.severity)}15` }}
                  >
                    <Icon
                      name={getEventTypeIcon(event?.event_type)}
                      size={20}
                      color={getSeverityColor(event?.severity)}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-medium text-foreground">
                        {event?.event_type?.replace(/_/g, ' ')?.toUpperCase()}
                      </h4>
                      <span
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={{
                          backgroundColor: `${getSeverityColor(event?.severity)}15`,
                          color: getSeverityColor(event?.severity)
                        }}
                      >
                        {event?.severity}
                      </span>
                      {event?.requires_immediate_action && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-error/15 text-error">
                          URGENT
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                      {event?.description}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Icon name="User" size={12} />
                        {event?.user?.full_name || 'Unknown'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="MapPin" size={12} />
                        {event?.ip_address || 'N/A'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Clock" size={12} />
                        {new Date(event?.created_at)?.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {/* Case Details */}
      <div className="card">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
          Case Details
        </h3>

        {selectedEvent ? (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Event Type</label>
              <p className="text-sm text-foreground mt-1">
                {selectedEvent?.event_type?.replace(/_/g, ' ')?.toUpperCase()}
              </p>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground">Description</label>
              <p className="text-sm text-foreground mt-1">{selectedEvent?.description}</p>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground">User</label>
              <p className="text-sm text-foreground mt-1">
                {selectedEvent?.user?.full_name || 'Unknown'} ({selectedEvent?.user?.email || 'N/A'})
              </p>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground">IP Address</label>
              <p className="text-sm text-foreground mt-1">{selectedEvent?.ip_address || 'N/A'}</p>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground">Location</label>
              <p className="text-sm text-foreground mt-1">{selectedEvent?.location || 'Unknown'}</p>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground">Timestamp</label>
              <p className="text-sm text-foreground mt-1">
                {new Date(selectedEvent?.created_at)?.toLocaleString()}
              </p>
            </div>

            {selectedEvent?.threat_indicators && Object.keys(selectedEvent?.threat_indicators)?.length > 0 && (
              <div>
                <label className="text-xs font-medium text-muted-foreground">Threat Indicators</label>
                <div className="mt-2 p-3 bg-muted/50 rounded-lg">
                  <pre className="text-xs text-foreground overflow-x-auto">
                    {JSON.stringify(selectedEvent?.threat_indicators, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            <div className="border-t border-border pt-4">
              <label className="text-xs font-medium text-muted-foreground block mb-2">
                Resolution Notes
              </label>
              <textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e?.target?.value)}
                placeholder="Enter resolution details..."
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                rows={4}
              />
              <Button
                onClick={handleResolveEvent}
                disabled={!resolutionNotes?.trim() || resolving}
                loading={resolving}
                className="w-full mt-3"
                size="sm"
              >
                <Icon name="CheckCircle" size={16} className="mr-2" />
                Resolve Case
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <Icon name="FolderOpen" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Select a case to view details</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncidentCaseManagement;